/**
 * The whole state of one run. Everything `apply` reads or changes lives here,
 * so a run is fully described by its initial state plus its command log, and
 * a floor can be resumed from its state alone (the random streams are inside).
 *
 * Plain data only: records, arrays, strings, integers, booleans, null. No
 * Map/Set, no classes, no undefined members, so `canonicalJson` hashes it.
 * The wiki page Architecture → game-core contract describes every field.
 */
import type { ContentPack } from '@ab/content-schema';
import { lookup } from './content.ts';
import { compareIds, sortedKeys } from './determinism.ts';
import type { Dir, Pos } from './grid.ts';
import { type RngState, seedRng } from './rng.ts';
import { type BossScaling, type Equipment, type StageStat, unitStat } from './stats.ts';

export type { BossScaling, Equipment, StageStat };

export type Side = 'party' | 'enemy';
/** `completed`, `failed` and `escaped` are terminal: no command is accepted after them. */
export type Phase = 'between_floors' | 'playing' | 'completed' | 'failed' | 'escaped';
export type Tile = 'wall' | 'floor' | 'exit';
export type Status = 'burn' | 'poison' | 'sleep' | 'paralysis' | 'confusion' | 'stun' | 'blind' | 'disrupt';
export type UnitKind = 'monster' | 'human';

export type Unit = {
  readonly id: string;
  readonly side: Side;
  readonly kind: UnitKind;
  /** Species id: `monster.*` for a monster, `human.*` for a human. */
  readonly monster: string;
  readonly level: number;
  readonly exp: number;
  /** One of the species' `passivePool`; null for a human (no passive ever triggers for it). */
  readonly passive: string | null;
  readonly skills: readonly string[];
  /** A human's weapon and armor item ids; null for a monster. */
  readonly equipment: Equipment | null;
  /** The dungeon's boss scaling for the last floor's boss; null for every other unit. */
  readonly boss: BossScaling | null;
  readonly pos: Pos;
  readonly facing: Dir;
  readonly hp: number;
  readonly ap: number;
  readonly sp: number;
  readonly stages: readonly { readonly stat: StageStat; readonly stages: number; readonly turns: number | null }[];
  readonly statuses: readonly { readonly status: Status; readonly turns: number }[];
  readonly charging: {
    readonly skill: string;
    readonly facing: Dir;
    readonly target: string | null;
    readonly turnsLeft: number;
  } | null;
  readonly firedOnce: readonly string[];
  /** Lost hp since the end of the unit's own previous turn; reset at the end of each of its turns (round.ts). */
  readonly tookDamageThisTurn: boolean;
  /**
   * Walking turns in a row toward natural regeneration (round.ts): a party unit's own turn spent
   * stepping, swapping or waiting adds one; an attack, skill, item or charge turn resets it to 0; a
   * skipped turn leaves it. 0 for enemies; reset on `enter_floor`.
   */
  readonly restTurns: number;
  readonly down: boolean;
};

export type Room = { readonly x: number; readonly y: number; readonly w: number; readonly h: number };

export type FloorItem = { readonly pos: Pos; readonly item: string; readonly count: number };

/** An unspent AP spring: a party unit below max AP that waits here restores `percent` of its max AP, once (round.ts). */
export type FloorSpring = { readonly pos: Pos; readonly percent: number };

export type Floor = {
  readonly width: number;
  readonly height: number;
  /** Row-major, `width × height`; index `y * width + x`. */
  readonly tiles: readonly Tile[];
  /** Interior rectangles, in placement order. */
  readonly rooms: readonly Room[];
  readonly items: readonly FloorItem[];
  /** Unspent AP springs, in row-major order; a spring leaves the list when it is used. */
  readonly springs: readonly FloorSpring[];
  readonly startRoom: number;
  /** The room holding the exit tile; on the last floor, which has no exit tile, the boss's room. */
  readonly exitRoom: number;
};

export type Streams = {
  readonly map: RngState;
  readonly combat: RngState;
  readonly ai: RngState;
  readonly loot: RngState;
};

export type BagEntry = { readonly item: string; readonly count: number };

export type RunState = {
  readonly schemaVersion: 1;
  readonly contentHash: string;
  readonly dungeon: string;
  readonly floorNumber: number;
  readonly phase: Phase;
  readonly turn: number;
  readonly rng: Streams;
  readonly nextUnitId: number;
  /** Unit ids in party order; `[0]` is the leader. */
  readonly party: readonly string[];
  /** Sorted by id (`compareUnitIds`). Party units persist across floors; enemies do not. */
  readonly units: readonly Unit[];
  readonly bag: readonly BagEntry[];
  readonly floor: Floor | null;
  /** The species id of the egg this run obtained by defeating the boss; null until then. */
  readonly egg: string | null;
};

/** Stream numbers, so a floor seed yields four independent streams. */
export const streamIndex = { map: 0, combat: 1, ai: 2, loot: 3 } as const;

export const seedStreams = (seed: number): Streams => ({
  map: seedRng(seed, streamIndex.map),
  combat: seedRng(seed, streamIndex.combat),
  ai: seedRng(seed, streamIndex.ai),
  loot: seedRng(seed, streamIndex.loot),
});

/** A monster in the party, as the caller describes it when a run starts. */
export type MonsterMember = {
  /** `monster.<name>`. */
  readonly monster: string;
  readonly level: number;
  /** Must be in the species' `passivePool`. */
  readonly passive: string;
  /** Optional: defaults to the learnset entries at or below `level`, newest first, up to `skillSlots`. */
  readonly skills?: readonly string[];
};

/** A human in the party (at most one). No passive and no skills: its skills are its weapon's. */
export type HumanMember = {
  /** `human.<name>`. */
  readonly monster: string;
  readonly level: number;
  /** Item ids: a `weapon` item and an `armor` item. */
  readonly equipment: Equipment;
};

/** One party member as the caller describes it when a run starts. */
export type PartyMember = MonsterMember | HumanMember;

/** Unit ids are zero-padded to 3 digits ("u007"); past 999 they grow ("u1000"), so compare them with `compareUnitIds`. */
export const unitId = (n: number): string => `u${String(n).padStart(3, '0')}`;

/** Id order of units: shorter ids first, then string order, so "u999" < "u1000" and "u007" < "u010". Total. */
export const compareUnitIds = (a: string, b: string): number => a.length - b.length || compareIds(a, b);

/**
 * Why game-core refused a command: one code per reason, a closed list, so a
 * client can say it in any language (the web shell's `ui.refusal.<code>`).
 * Additive to the contract: the English `message` is unchanged and stays what
 * the terminal client, the replay report and the tests read.
 */
export const refusalCodes = [
  // targeting
  'needs_target',
  'no_standing_unit',
  'takes_no_target',
  'target_out_of_range',
  'target_out_of_reach',
  'target_not_allowed',
  'line_not_orthogonal',
  // skills and items
  'unit_cannot_act',
  'skill_not_known',
  'unknown_skill',
  'unit_charging',
  'not_enough_ap',
  'not_enough_sp',
  'disrupted_cannot_charge',
  'item_not_in_bag',
  'item_acts_by_itself',
  'item_not_usable_in_dungeon',
  'effect_not_modelled',
  'damage_without_style',
  'escape_leader_only',
  'nobody_to_heal',
  // moving, attacking, the floor
  'attack_around_corner',
  'attack_ally',
  'cannot_step',
  'bad_direction',
  'not_on_exit',
  'last_floor_no_exit',
  'leader_down',
  'leader_can_only_wait',
  'charging_leader',
  'run_not_playing',
  'not_between_floors',
  'no_floor',
  'no_unit',
  'empty_party',
  'party_unit_missing',
  'unknown_dungeon',
  'bad_seed',
  'no_room_for_party',
  'rooms_do_not_fit',
  'no_tile_for_boss',
  'no_room_for_springs',
  // the command's shape
  'command_not_object',
  'command_not_plain',
  'unknown_command_type',
  'command_unknown_field',
  'content_mismatch',
  'item_not_an_id',
  'skill_not_an_id',
  'target_not_unit_id',
  // the party and the bag a run starts with
  'dungeon_not_an_id',
  'party_not_list',
  'member_needs_species',
  'member_unknown_field',
  'passive_not_an_id',
  'skills_not_list',
  'too_many_humans',
  'party_size',
  'human_must_lead',
  'level_range',
  'unknown_monster',
  'needs_equipment',
  'equipment_unknown_field',
  'weapon_not_an_id',
  'armor_not_an_id',
  'unknown_weapon',
  'unknown_armor',
  'not_a_weapon',
  'not_an_armor',
  'too_many_skills',
  'monster_takes_no_equipment',
  'passive_not_in_pool',
  'skill_listed_twice',
  'skill_not_learned',
  'bag_not_list',
  'bag_too_many_stacks',
  'bag_stack_unknown_field',
  'bag_stack_needs_item',
  'unknown_item',
  'item_worn_not_carried',
  'stack_count',
  'item_listed_twice',
] as const;

export type RefusalCode = (typeof refusalCodes)[number];

/**
 * A refusal's values: content ids (`skill`, `item`, `monster`, `passive`,
 * `dungeon`), a unit id (`unit`), numbers, and raw input (`type`, `command`,
 * `field`, `effect`).
 */
export type RefusalArgs = Readonly<Record<string, string | number>>;

export class InvalidCommand extends Error {
  override readonly name = 'InvalidCommand';
  readonly code: RefusalCode;
  readonly args: RefusalArgs;
  constructor(code: RefusalCode, message: string, args: RefusalArgs = {}) {
    super(message);
    this.code = code;
    this.args = args;
  }
}

/** Placed at a dummy position until the first `enter_floor`. */
const unplaced: Pos = { x: 0, y: 0 };

/** Learnset entries at or below `level`, in learnset order, the last `skillSlots` of them. */
export const startingSkills = (content: ContentPack, monster: string, level: number): string[] => {
  const species = lookup(content.monsters, monster);
  if (species === null) throw new InvalidCommand('unknown_monster', `unknown monster ${monster}`, { monster });
  const learned = species.learnset.filter((e) => e.level <= level).map((e) => e.skill);
  const slots = content.rules.dungeon.skillSlots;
  return learned.slice(Math.max(0, learned.length - slots));
};

/** A party unit as `createRun` builds it: at full HP and AP, not yet placed. */
const newPartyUnit = (
  content: ContentPack,
  n: number,
  kind: UnitKind,
  m: { readonly monster: string; readonly level: number },
  passive: string | null,
  skills: readonly string[],
  equipment: Equipment | null,
): Unit => {
  const base = { monster: m.monster, level: m.level, equipment, boss: null };
  return {
    id: unitId(n),
    side: 'party',
    kind,
    monster: m.monster,
    level: m.level,
    exp: 0,
    passive,
    skills,
    equipment,
    boss: null,
    pos: unplaced,
    facing: 'n',
    hp: unitStat(content, base, 'hp'),
    ap: unitStat(content, base, 'ap'),
    sp: 0,
    stages: [],
    statuses: [],
    charging: null,
    firedOnce: [],
    tookDamageThisTurn: false,
    restTurns: 0,
    down: false,
  };
};

/** Throws unless every key of a party member (as it arrived, possibly JSON) is one of `allowed`. */
const assertMemberKeys = (m: object, allowed: readonly string[], what: string, code: RefusalCode, args: RefusalArgs = {}): void => {
  for (const key of sortedKeys(m as Record<string, unknown>)) {
    if (!allowed.includes(key)) throw new InvalidCommand(code, `${what} takes no field ${key}`, { ...args, field: key });
  }
};

/** The item id in an equipment slot, checked: a known item of the slot's category. */
const equipmentItem = (content: ContentPack, value: unknown, slot: 'weapon' | 'armor'): string => {
  const armor = slot === 'armor';
  if (typeof value !== 'string') throw new InvalidCommand(armor ? 'armor_not_an_id' : 'weapon_not_an_id', `${slot} must be an item id`);
  const item = lookup(content.items, value);
  if (item === null) throw new InvalidCommand(armor ? 'unknown_armor' : 'unknown_weapon', `unknown ${slot} ${value}`, { item: value });
  if (item.category !== slot) throw new InvalidCommand(armor ? 'not_an_armor' : 'not_a_weapon', `${value} is not ${armor ? 'an armor' : 'a weapon'}`, { item: value });
  return value;
};

/** A human party member: level, then equipment (a weapon and an armor); its skills are the weapon's. */
const humanUnit = (content: ContentPack, n: number, m: PartyMember): Unit => {
  assertMemberKeys(m, ['monster', 'level', 'equipment'], `human ${m.monster}`, 'member_unknown_field', { monster: m.monster });
  const given: unknown = 'equipment' in m ? m.equipment : undefined;
  if (typeof given !== 'object' || given === null || Array.isArray(given)) throw new InvalidCommand('needs_equipment', `${m.monster} needs equipment: a weapon and an armor`, { monster: m.monster });
  assertMemberKeys(given, ['weapon', 'armor'], 'equipment', 'equipment_unknown_field');
  const slots = given as Record<string, unknown>;
  const equipment: Equipment = { weapon: equipmentItem(content, slots['weapon'], 'weapon'), armor: equipmentItem(content, slots['armor'], 'armor') };
  const skills = lookup(content.items, equipment.weapon)?.equip?.skills ?? [];
  if (skills.length > content.rules.dungeon.skillSlots) throw new InvalidCommand('too_many_skills', 'too many skills', { slots: content.rules.dungeon.skillSlots });
  return newPartyUnit(content, n, 'human', m, null, [...skills], equipment);
};

/** A monster party member: passive from its pool, skills given or from its learnset. */
const monsterUnit = (content: ContentPack, n: number, m: PartyMember): Unit => {
  if ('equipment' in m) throw new InvalidCommand('monster_takes_no_equipment', `${m.monster} is a monster and takes no equipment`, { monster: m.monster });
  assertMemberKeys(m, ['monster', 'level', 'passive', 'skills'], `monster ${m.monster}`, 'member_unknown_field', { monster: m.monster });
  const species = lookup(content.monsters, m.monster);
  if (species === null) throw new InvalidCommand('unknown_monster', `unknown monster ${m.monster}`, { monster: m.monster });
  const passive: unknown = 'passive' in m ? m.passive : undefined;
  if (typeof passive !== 'string' || !species.passivePool.includes(passive)) throw new InvalidCommand('passive_not_in_pool', `${m.monster} cannot have passive ${String(passive)}`, { monster: m.monster, passive: String(passive) });
  // Party members can arrive as JSON (a golden log), so the list is checked at run time.
  const given: unknown = 'skills' in m ? m.skills : undefined;
  if (given !== undefined && !Array.isArray(given)) throw new InvalidCommand('skills_not_list', 'skills must be a list of skill ids');
  const skills: unknown[] = Array.isArray(given) ? [...(given as unknown[])] : startingSkills(content, m.monster, m.level);
  // Given skills must be distinct learnset entries at or below the unit's level.
  const learned = species.learnset.filter((e) => e.level <= m.level).map((e) => e.skill);
  const checked = skills.map((s, j): string => {
    if (typeof s !== 'string' || lookup(content.skills, s) === null) throw new InvalidCommand('unknown_skill', `unknown skill ${String(s)}`, { skill: String(s) });
    if (skills.indexOf(s) !== j) throw new InvalidCommand('skill_listed_twice', `skill ${s} is listed twice`, { skill: s });
    if (!learned.includes(s)) throw new InvalidCommand('skill_not_learned', `${m.monster} at level ${m.level} has not learned ${s}`, { monster: m.monster, level: m.level, skill: s });
    return s;
  });
  if (checked.length > content.rules.dungeon.skillSlots) throw new InvalidCommand('too_many_skills', 'too many skills', { slots: content.rules.dungeon.skillSlots });
  return newPartyUnit(content, n, 'monster', m, passive, checked, null);
};

/**
 * Throws unless every id a party member carries (as it arrived, possibly JSON)
 * is a string: `monster`, `passive` when present, each entry of `skills` when
 * it is a list, and `equipment.weapon` / `equipment.armor` when present. Runs
 * before any id is looked up, so a list or an object where an id belongs is
 * refused rather than coerced to a key.
 */
const assertMemberIdTypes = (m: Record<string, unknown>): void => {
  if (typeof m['monster'] !== 'string') throw new InvalidCommand('member_needs_species', 'a party member needs a species id (monster)');
  if ('passive' in m && typeof m['passive'] !== 'string') throw new InvalidCommand('passive_not_an_id', 'passive must be a passive id');
  const skills = m['skills'];
  if (Array.isArray(skills) && !skills.every((s: unknown) => typeof s === 'string')) throw new InvalidCommand('skills_not_list', 'skills must be a list of skill ids');
  const equipment = m['equipment'];
  if (typeof equipment === 'object' && equipment !== null && !Array.isArray(equipment)) {
    const slots = equipment as Record<string, unknown>;
    if ('weapon' in slots && typeof slots['weapon'] !== 'string') throw new InvalidCommand('weapon_not_an_id', 'weapon must be an item id');
    if ('armor' in slots && typeof slots['armor'] !== 'string') throw new InvalidCommand('armor_not_an_id', 'armor must be an item id');
  }
};

/**
 * The bag a run starts with, checked (it can arrive as JSON): a list of at
 * most `bagSlots` stacks `{ item, count }`, each item known and not
 * equipment (weapons and armor are worn, not carried), each count
 * 1..the item's `stackSize.dungeon`, and each item once (merging stacks is
 * the caller's job). The stacks keep their order.
 */
const startingBag = (content: ContentPack, bag: unknown): BagEntry[] => {
  if (!Array.isArray(bag)) throw new InvalidCommand('bag_not_list', 'the bag is a list of { item, count } stacks');
  const stacks = bag as unknown[];
  const slots = content.rules.dungeon.bagSlots;
  if (stacks.length > slots) throw new InvalidCommand('bag_too_many_stacks', `the bag holds at most ${slots} stacks`, { slots });
  const seen: string[] = [];
  // Array.from visits holes too (map would skip them), so a sparse bag is refused here and never reaches the run.
  return Array.from(stacks, (e): BagEntry => {
    if (typeof e !== 'object' || e === null || Array.isArray(e)) throw new InvalidCommand('bag_not_list', 'the bag is a list of { item, count } stacks');
    assertMemberKeys(e, ['item', 'count'], 'a bag stack', 'bag_stack_unknown_field');
    const { item, count } = e as Record<string, unknown>;
    if (typeof item !== 'string') throw new InvalidCommand('bag_stack_needs_item', 'a bag stack needs an item id');
    const def = lookup(content.items, item);
    if (def === null) throw new InvalidCommand('unknown_item', `unknown item ${item}`, { item });
    if (def.category === 'weapon' || def.category === 'armor') throw new InvalidCommand('item_worn_not_carried', `${item} is worn, not carried in the bag`, { item });
    const max = def.stackSize.dungeon;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 1 || count > max) throw new InvalidCommand('stack_count', `a stack of ${item} holds 1..${max}`, { item, max });
    if (seen.includes(item)) throw new InvalidCommand('item_listed_twice', `${item} is listed twice in the bag`, { item });
    seen.push(item);
    return { item, count };
  });
};

/**
 * The state a run starts in: the party is created at full HP and AP, the bag
 * holds `bag` (checked by `startingBag`; empty by default; the play client
 * passes the new-player gift, `rules.newPlayer.starterBag`), no floor yet.
 * `enter_floor` with the server's per-floor seed comes next. The streams
 * are seeded from 0 until then so the state is complete. A party is 1 to
 * `partySize` monsters plus at most one human (a party of only the human is
 * allowed); a human must lead (`party[0]`). A party without a human is
 * allowed too (tests, future autonomous runs).
 */
export const createRun = (content: ContentPack, dungeon: string, party: readonly PartyMember[], bag: readonly BagEntry[] = []): RunState => {
  // The dungeon id and the party members can arrive as JSON (a golden log), so their shape is checked at run
  // time, every id's type before any id is looked up.
  const dungeonId: unknown = dungeon;
  if (typeof dungeonId !== 'string') throw new InvalidCommand('dungeon_not_an_id', 'the dungeon must be a dungeon id');
  const given: unknown = party;
  if (!Array.isArray(given) || !given.every((m: unknown) => typeof m === 'object' && m !== null && !Array.isArray(m))) {
    throw new InvalidCommand('party_not_list', 'the party is a list of party members');
  }
  for (const m of given as object[]) assertMemberIdTypes(m as Record<string, unknown>);
  if (lookup(content.dungeons, dungeon) === null) throw new InvalidCommand('unknown_dungeon', `unknown dungeon ${dungeon}`, { dungeon });
  const isHuman = (m: PartyMember): boolean => lookup(content.humans, m.monster) !== null;
  const humans = party.filter(isHuman).length;
  const monsters = party.length - humans;
  if (humans > 1) throw new InvalidCommand('too_many_humans', 'a party has at most one human');
  if (party.length < 1 || monsters > content.rules.combat.partySize) {
    throw new InvalidCommand('party_size', `a party is 1..${content.rules.combat.partySize} monsters plus at most one human, and not empty`, { size: content.rules.combat.partySize });
  }
  const first = party[0];
  if (humans === 1 && (first === undefined || !isHuman(first))) throw new InvalidCommand('human_must_lead', 'a human must lead the party');
  const units: Unit[] = party.map((m, i) => {
    if (!Number.isInteger(m.level) || m.level < 1 || m.level > content.rules.combat.levelCap) {
      throw new InvalidCommand('level_range', `level must be 1..${content.rules.combat.levelCap}`, { cap: content.rules.combat.levelCap });
    }
    return isHuman(m) ? humanUnit(content, i + 1, m) : monsterUnit(content, i + 1, m);
  });
  const stacks = startingBag(content, bag);
  return {
    schemaVersion: 1,
    contentHash: content.hash,
    dungeon,
    floorNumber: 0,
    phase: 'between_floors',
    turn: 0,
    rng: seedStreams(0),
    nextUnitId: units.length + 1,
    party: units.map((u) => u.id),
    units: [...units].sort((a, b) => compareUnitIds(a.id, b.id)),
    bag: stacks,
    floor: null,
    egg: null,
  };
};

/** One of `itemId` leaves the bag: the first stack holding it shrinks, and goes when empty. */
export const takeOneFromBag = (state: RunState, itemId: string): RunState => {
  const i = state.bag.findIndex((e) => e.item === itemId && e.count > 0);
  return { ...state, bag: state.bag.flatMap((e, j) => (j !== i ? [e] : e.count > 1 ? [{ ...e, count: e.count - 1 }] : [])) };
};

/** The unit with this id, or throws. */
export const unitById = (state: RunState, id: string): Unit => {
  const u = state.units.find((x) => x.id === id);
  if (!u) throw new InvalidCommand('no_unit', `no unit ${id}`, { unit: id });
  return u;
};

export const leaderOf = (state: RunState): Unit => {
  const id = state.party[0];
  if (id === undefined) throw new InvalidCommand('empty_party', 'empty party');
  return unitById(state, id);
};

/** A copy of the state with one unit replaced (matched by id). */
export const withUnit = (state: RunState, unit: Unit): RunState => ({
  ...state,
  units: state.units.map((u) => (u.id === unit.id ? unit : u)),
});

/** A copy of the state with one unit changed by `fn` (matched by id). */
export const updateUnit = (state: RunState, id: string, fn: (u: Unit) => Unit): RunState => ({
  ...state,
  units: state.units.map((u) => (u.id === id ? fn(u) : u)),
});

/** The unit with this id, or null if it is gone (enemies are removed when they fall). */
export const findUnit = (state: RunState, id: string): Unit | null => state.units.find((u) => u.id === id) ?? null;

export const hasStatus = (u: Unit, status: Status): boolean => u.statuses.some((s) => s.status === status);

/** Throws unless the run is in `phase`. */
export const assertPhase = (state: RunState, phase: 'playing' | 'between_floors'): void => {
  if (state.phase !== phase) throw new InvalidCommand(phase === 'playing' ? 'run_not_playing' : 'not_between_floors', `expected phase ${phase}, run is ${state.phase}`);
};

/** The current floor, or throws. */
export const floorOf = (state: RunState): Floor => {
  if (state.floor === null) throw new InvalidCommand('no_floor', 'no floor');
  return state.floor;
};

/** A copy of the state with one stream replaced. */
export const withRng = (state: RunState, stream: keyof Streams, next: RngState): RunState => ({
  ...state,
  rng: { ...state.rng, [stream]: next },
});

/**
 * Whether party unit `unitId` may swap places with party unit `other` when it
 * steps into it: the leader with any ally; an ally only with an ally earlier
 * in party order (one that has already had its turn this round), never with
 * the leader, and never with a charging ally (the swap would move it off the
 * tile its charge was aimed from). The leader's swap with a charging ally
 * interrupts that charge (round.ts `moveUnit`).
 */
export const maySwapWith = (state: RunState, unitId: string, other: string): boolean => {
  if (unitId === state.party[0]) return true;
  const i = state.party.indexOf(other);
  if (!(i > 0 && i < state.party.indexOf(unitId))) return false;
  const o = state.units.find((x) => x.id === other);
  return o !== undefined && o.charging === null;
};
